import useAPI from "@toothfairy/shared-api/useApi";
import { AppText } from "@toothfairy/shared-ui";
import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTab from "@toothfairy/shared-ui/AppTab";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { copyToClipboard } from "@toothfairy/shared-ui/ClipboardUtils";
import axios from "axios";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import envConfig from "../../../../envConfig";
import useWorkspace from "../../../Hooks/useWorkspaces";
import Files from "../../../API/Files";
import trainingAPI from "../../../API/Training/trainingAPI";
import workspacesHelper from "../../../API/Workspaces/workspacesHelper";

import AdminInfoLeaf from "./SubComponents/AdminInfoLeaf";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../../Components/ToothFairyComponents/ToothFairyModal";

const CodeSnippet = ({ title, subtitle, code, onCopy, theme, t }) => {
  return (
    <View
      style={{
        width: "100%",
        marginTop: 16,
        padding: 12,
        borderRadius: 8,
        backgroundColor: theme?.theme?.cardBackground,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <AppText wrapperStyle={{ flex: 1 }} fontWeight="bold">
          {title}
        </AppText>
        <ToothFairyButton
          title={t("admin.apiIntegration.copyButton")}
          onPress={() => onCopy(code)}
        />
      </View>
      {subtitle ? (
        <AppText wrapperStyle={{ marginTop: 4 }}>{subtitle}</AppText>
      ) : null}
      <View
        style={{
          marginTop: 10,
          padding: 10,
          borderRadius: 6,
          backgroundColor: theme?.theme?.message_bubble_background,
        }}
      >
        <AppText
          wrapperStyle={{ fontFamily: "monospace", fontSize: 12 }}
          color={theme?.theme?.primary}
        >
          {code}
        </AppText>
      </View>
    </View>
  );
};

const ApiIntegrationView = () => {
  const {
    getActiveWorkspace,
    isTF_API_AccessEnabled,
    updateWorkspaceInContext,
  } = useWorkspace();
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  const activeWorkspace = getActiveWorkspace();
  const workspaceId = activeWorkspace?.id;
  const apiKey = activeWorkspace?.workspaceToken;

  // Workspace state can churn (context re-dispatches / subscription updates)
  // and briefly resolve getActiveWorkspace() to undefined on interleaved
  // renders. Left unchecked, workspaceId/apiKey flip real <-> undefined each
  // render, which both makes the token + integration snippets flicker and
  // changes the useCallback identities below so the tfcode effects re-fire in
  // a loop (the "api key changing erratically" symptom). Latch the last known
  // good id/token so the derived values stay stable across that churn.
  const lastWorkspaceIdRef = useRef(workspaceId);
  const lastApiKeyRef = useRef(apiKey);
  const stableWorkspaceId = workspaceId ?? lastWorkspaceIdRef.current;
  const stableApiKey = apiKey ?? lastApiKeyRef.current;
  useEffect(() => {
    if (workspaceId) lastWorkspaceIdRef.current = workspaceId;
    if (apiKey) lastApiKeyRef.current = apiKey;
  }, [workspaceId, apiKey]);

  const handleRotateApiKey = async () => {
    setIsRotating(true);
    try {
      const result = await workspacesHelper.rotateApiKey(
        stableWorkspaceId,
        stableApiKey,
      );

      if (result) {
        const updatedWorkspace = await workspacesHelper.getWorkspaceByID(
          stableWorkspaceId,
        );
        if (updatedWorkspace) {
          updateWorkspaceInContext(updatedWorkspace);
        }
        AppToasty.show(t("admin.apiIntegration.rotateSuccess"), {
          type: "success",
        });
        setShowRotateModal(false);
      }
    } catch (error) {
      console.error("Error rotating API key:", error);
      AppToasty.show(t("admin.apiIntegration.rotateError"), { type: "danger" });
    } finally {
      setIsRotating(false);
    }
  };

  const TFCODE_FILE_NAME = "tfcode.json";

  // The upload endpoint (requestUploadURL + importType="imported-files")
  // writes the object to "imported-files/tfcode/<id>/tfcode.json" but expects
  // the UNPREFIXED filename. The download endpoint (requestDownloadURL) does
  // NOT add that prefix, so it must be given the FULL object path to resolve
  // the same file.
  const getTfcodeUploadKey = useCallback(() => {
    return `tfcode/${stableWorkspaceId}/${TFCODE_FILE_NAME}`;
  }, [stableWorkspaceId]);

  const getTfcodeKey = useCallback(() => {
    return `imported-files/tfcode/${stableWorkspaceId}/${TFCODE_FILE_NAME}`;
  }, [stableWorkspaceId]);

  const { apiRequest: tfcodeUploadUrlRequest } = useAPI(
    trainingAPI.bulkImportUrlGeneration,
    envConfig,
  );
  const { apiRequest: tfcodeDownloadUrlRequest } = useAPI(
    Files.downloadUrlGeneration,
    envConfig,
  );

  const tfcodeDownloadUrlRef = useRef(tfcodeDownloadUrlRequest);
  useEffect(() => {
    tfcodeDownloadUrlRef.current = tfcodeDownloadUrlRequest;
  });

  const [tfcodeStatus, setTfcodeStatus] = useState({
    exists: false,
    lastModified: null,
    loading: true,
  });
  const [tfcodeContent, setTfcodeContent] = useState(null);
  const [isTfcodeContentLoading, setIsTfcodeContentLoading] = useState(false);
  const [isTfcodeDownloading, setIsTfcodeDownloading] = useState(false);
  const [isTfcodeUploading, setIsTfcodeUploading] = useState(false);
  const [tfcodeUploadProgress, setTfcodeUploadProgress] = useState(0);

  const loadTfcodeStatus = useCallback(async () => {
    if (!stableWorkspaceId) {
      setTfcodeStatus({ exists: false, lastModified: null, loading: false });
      return;
    }
    setTfcodeStatus((prev) => ({ ...prev, loading: true }));
    try {
      const result = await tfcodeDownloadUrlRef.current({
        workspaceid: stableWorkspaceId,
        filename: getTfcodeKey(),
        type: "imported-files",
        context: "tfcode",
      });
      const url = result?.url;
      if (!url) {
        console.warn("loadTfcodeStatus: no presigned URL returned", result);
        setTfcodeStatus({ exists: false, lastModified: null, loading: false });
        return;
      }
      // Presigned URLs may not accept HEAD, so check existence with a GET and
      // cancel the body immediately to avoid downloading the whole file.
      console.log("loadTfcodeStatus: checking existence via GET", {
        url: url.slice(0, 80),
        workspaceId: stableWorkspaceId,
        key: getTfcodeKey(),
      });
      const response = await fetch(url);
      console.log("loadTfcodeStatus: GET response", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
        lastModified: response.headers.get("last-modified"),
      });
      if (response.ok) {
        await response.body?.cancel?.();
        setTfcodeStatus({
          exists: true,
          lastModified: response.headers.get("last-modified"),
          loading: false,
        });
      } else {
        setTfcodeStatus({ exists: false, lastModified: null, loading: false });
      }
    } catch (error) {
      console.error("loadTfcodeStatus: error", error, {
        workspaceId: stableWorkspaceId,
        key: getTfcodeKey(),
      });
      setTfcodeStatus({ exists: false, lastModified: null, loading: false });
    }
  }, [stableWorkspaceId, getTfcodeKey]);

  const loadTfcodeContent = useCallback(async () => {
    if (!stableWorkspaceId) return;
    setIsTfcodeContentLoading(true);
    try {
      const result = await tfcodeDownloadUrlRef.current({
        workspaceid: stableWorkspaceId,
        filename: getTfcodeKey(),
        type: "imported-files",
        context: "tfcode",
      });
      const url = result?.url;
      if (!url) {
        setTfcodeContent(null);
        return;
      }
      const response = await fetch(url);
      if (response.ok) {
        const text = await response.text();
        setTfcodeContent(text);
      } else {
        setTfcodeContent(null);
      }
    } catch (error) {
      console.error("loadTfcodeContent: error", error);
      setTfcodeContent(null);
    } finally {
      setIsTfcodeContentLoading(false);
    }
  }, [stableWorkspaceId, getTfcodeKey]);

  useEffect(() => {
    loadTfcodeStatus();
  }, [loadTfcodeStatus]);

  useEffect(() => {
    if (tfcodeStatus?.exists && !tfcodeStatus?.loading) {
      loadTfcodeContent();
    } else {
      setTfcodeContent(null);
    }
  }, [tfcodeStatus?.exists, tfcodeStatus?.loading, loadTfcodeContent]);

  const handleTfcodeDownload = async () => {
    try {
      setIsTfcodeDownloading(true);
      const workspaceid = stableWorkspaceId;
      const result = await tfcodeDownloadUrlRequest({
        workspaceid,
        filename: getTfcodeKey(),
        type: "imported-files",
        context: "tfcode",
      });
      const url = result?.url;
      if (!url) {
        AppToasty.show(t("admin.apiIntegration.tfcodeDownloadError"), {
          type: "danger",
        });
        return;
      }
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = TFCODE_FILE_NAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading tfcode.json:", error);
      AppToasty.show(t("admin.apiIntegration.tfcodeDownloadError"), {
        type: "danger",
      });
    } finally {
      setIsTfcodeDownloading(false);
    }
  };

  const handleTfcodeUpload = async (file) => {
    if (!file) return;
    try {
      setIsTfcodeUploading(true);
      setTfcodeUploadProgress(0);
      const result = await tfcodeUploadUrlRequest({
        filename: getTfcodeUploadKey(),
        importType: "imported-files",
        contentType: "application/json",
      });
      console.log("handleTfcodeUpload: got presigned upload URL", {
        key: getTfcodeUploadKey(),
        body: result?.body,
      });
      const uploadResponse = await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: file,
        headers: {
          "Content-Type": "application/json",
        },
        onUploadProgress: (p) => {
          const progress = p.total ? Math.round((p.loaded * 100) / p.total) : 0;
          setTfcodeUploadProgress(progress);
        },
      });
      console.log("handleTfcodeUpload: upload done", {
        status: uploadResponse?.status,
        size: file?.size,
      });
      await loadTfcodeStatus();
      await loadTfcodeContent();
      AppToasty.show(t("admin.apiIntegration.tfcodeUploadSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.error("Error uploading tfcode.json:", error);
      AppToasty.show(t("admin.apiIntegration.tfcodeUploadError"), {
        type: "danger",
      });
    } finally {
      setIsTfcodeUploading(false);
    }
  };

  const triggerTfcodeUpload = () => {
    const fileSelector = document.createElement("input");
    fileSelector.setAttribute("type", "file");
    fileSelector.setAttribute("accept", ".json,application/json");
    fileSelector.addEventListener("change", (e) => {
      const file = e.target?.files?.[0];
      if (file) handleTfcodeUpload(file);
      fileSelector.value = "";
    });
    fileSelector.click();
  };

  const handleCopySnippet = async (text) => {
    const success = await copyToClipboard(text);
    AppToasty.show(
      success
        ? t("admin.apiIntegration.copySuccess")
        : t("admin.apiIntegration.copyError"),
      { type: success ? "success" : "danger" },
    );
  };

  const baseUrl = envConfig.REACT_APP_AIS_ENDPOINT;

  const tfcodeCliSnippet = `# 1. Add a TF Code profile for this workspace\ntfcode setup --profile ${stableWorkspaceId} --workspace-id ${stableWorkspaceId} --api-key ${stableApiKey} --region au\n\n# 2. Use it (or set these in .tfcode/tfcode.json)\ntfcode -p ${stableWorkspaceId} run "Your task here"`;

  const claudeCodeSnippet = `{
  "env": {
    "ANTHROPIC_BASE_URL": "${baseUrl}",
    "ANTHROPIC_AUTH_TOKEN": "${stableApiKey}",
    "ANTHROPIC_MODEL": "sorcerer",
    "ANTHROPIC_SMALL_FAST_MODEL": "sorcerer",
    "ANTHROPIC_DEFAULT_SONNET_MODEL": "sorcerer",
    "ANTHROPIC_DEFAULT_HAIKU_MODEL": "sorcerer",
    "ANTHROPIC_DEFAULT_OPUS_MODEL": "sorcerer"
  }
}`;

  const curlSnippet = `curl -N -X POST ${baseUrl}/v1/chat/completions \\\n  -H "Authorization: Bearer ${stableApiKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"sorcerer","messages":[{"role":"user","content":"Hello!"}],"stream":true}'`;

  const openaiEnvSnippet = `OPENAI_API_KEY=${stableApiKey}
OPENAI_BASE_URL=${baseUrl}/v1`;

  const codexSnippet = `# ~/.codex/config.toml
model_provider = "toothfairy"
model = "sorcerer"

[model_providers.toothfairy]
name = "ToothFairyAI"
base_url = "${baseUrl}/v1"
env_key = "TOOTHFAIRYAI_API_KEY"

# then:\nexport TOOTHFAIRYAI_API_KEY=${stableApiKey}`;

  const clineSnippet = `In the Cline extension (VS Code):

API Provider : OpenAI Compatible
Base URL     : ${baseUrl}/v1
API Key      : ${stableApiKey}
Model ID     : sorcerer

Save settings, then start using Cline.`;

  const kiloCodeSnippet = `In the Kilo Code extension (VS Code):

API Provider : OpenAI Compatible
Base URL     : ${baseUrl}/v1
API Key      : ${stableApiKey}
Model        : sorcerer

Save settings, then start using Kilo Code.`;

  const tfcodeJsonSnippet = `{\n  "toothfairy": {\n    "workspace_id": "${stableWorkspaceId}",\n    "api_key": "${stableApiKey}",\n    "region": "au"\n  },\n  "model": "toothfairyai/sorcerer",\n  "default_agent": "build"\n}`;

  const renderSnippets = (snippets) => (
    <View style={{ width: "100%", paddingTop: 4 }}>
      {snippets.map((s) => (
        <CodeSnippet
          key={s.code}
          title={s.title}
          subtitle={s.subtitle}
          code={s.code}
          onCopy={handleCopySnippet}
          theme={currentTheme}
          t={t}
        />
      ))}
    </View>
  );

  const connectRoutes = [
    { key: "tfcode", title: t("admin.apiIntegration.tabTfCode") },
    { key: "claude", title: "Claude Code" },
    { key: "cursor", title: "Cursor" },
    { key: "cline", title: "Cline" },
    { key: "kilo", title: "Kilo Code" },
    { key: "codex", title: "Codex" },
    { key: "rest", title: t("admin.apiIntegration.tabApi") },
  ];

  const connectScenes = {
    tfcode: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetCliTitle"),
          subtitle: t("admin.apiIntegration.snippetCliSubtitle"),
          code: tfcodeCliSnippet,
        },
        {
          title: t("admin.apiIntegration.snippetTfcodeJsonTitle"),
          subtitle: t("admin.apiIntegration.snippetTfcodeJsonSubtitle"),
          code: tfcodeJsonSnippet,
        },
      ]),
    claude: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetClaudeCodeTitle"),
          subtitle: t("admin.apiIntegration.snippetClaudeCodeSubtitle"),
          code: claudeCodeSnippet,
        },
      ]),
    cursor: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetCursorTitle"),
          subtitle: t("admin.apiIntegration.snippetCursorSubtitle"),
          code: openaiEnvSnippet,
        },
      ]),
    cline: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetClineTitle"),
          subtitle: t("admin.apiIntegration.snippetClineSubtitle"),
          code: clineSnippet,
        },
      ]),
    kilo: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetKiloTitle"),
          subtitle: t("admin.apiIntegration.snippetKiloSubtitle"),
          code: kiloCodeSnippet,
        },
      ]),
    codex: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetCodexTitle"),
          subtitle: t("admin.apiIntegration.snippetCodexSubtitle"),
          code: codexSnippet,
        },
      ]),
    rest: () =>
      renderSnippets([
        {
          title: t("admin.apiIntegration.snippetCurlTitle"),
          subtitle: t("admin.apiIntegration.snippetApiSubtitle"),
          code: curlSnippet,
        },
        {
          title: t("admin.apiIntegration.snippetOpenAiTitle"),
          subtitle: t("admin.apiIntegration.snippetOpenAiSubtitle"),
          code: openaiEnvSnippet,
        },
      ]),
  };

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ paddingBottom: 30 }}
    >
      <View
        style={{
          width: "100%",
          maxWidth: 750,
          marginHorizontal: "auto",
          paddingHorizontal: 20,
          paddingVertical: 10,
        }}
      >
        <AdminInfoLeaf
          wrapperStyle={{
            alignSelf: "flex-start",
            width: "100%",
            gap: 5,
          }}
          captionStyle={{
            alignSelf: "flex-start",
          }}
          textStyle={{
            marginLeft: 10,
          }}
          caption={t("admin.apiIntegration.workspaceIdCaption")}
          subcaption={
            isTF_API_AccessEnabled()
              ? t("admin.apiIntegration.workspaceIdSubCaption")
              : t("legacy.workspaceIdNotEnabledCaption")
          }
          subCaptionColor={
            !isTF_API_AccessEnabled() && currentTheme?.theme?.primary
          }
          title={t("admin.apiIntegration.workspaceIdTitle")}
          accessibilityLabel={"WorkspaceId"}
          value={stableWorkspaceId}
          showSimpleText
          iconSize={14}
        />
        <AdminInfoLeaf
          wrapperStyle={{
            alignSelf: "flex-start",
            width: "100%",
            gap: 5,
          }}
          captionStyle={{
            alignSelf: "flex-start",
          }}
          textStyle={{
            marginLeft: 10,
          }}
          value={stableApiKey}
          caption={t("admin.apiIntegration.apiKeyCaption")}
          subcaption={t("admin.apiIntegration.apiKeySubCaption")}
          title={t("admin.apiIntegration.workspaceTokenTitle")}
          accessibilityLabel={"WorkspaceToken"}
          hideShowIcon={true}
          showSimpleText
          showSecret
          iconSize={14}
          actionIcon={AppAssets.icons.reload}
          onActionIconPress={() => setShowRotateModal(true)}
          actionIconAccessibilityLabel={t("admin.apiIntegration.rotateButton")}
        />
        <AppExpander
          style={{ width: "100%", marginTop: 24 }}
          isExpandedOnLoad={true}
          title={t("admin.apiIntegration.sectionTfCodeIntegrationsTitle")}
          icon={AppAssets.icons.code}
        >
          <AppText
            wrapperStyle={{ marginTop: 4, marginBottom: 8, paddingLeft: 0 }}
            style={{
              paddingLeft: 10,
            }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("admin.apiIntegration.sectionTfCodeIntegrationsCaption")}
          </AppText>

          <View
            style={{
              alignSelf: "stretch",
              width: "100%",
              marginTop: 12,
              padding: 20,
              borderRadius: 12,
              backgroundColor: currentTheme?.theme?.cardBackground,
              marginHorizontal: 10,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <AppText
                wrapperStyle={{ flex: 1, marginBottom: 0 }}
                fontWeight="bold"
                fontSize={16}
                color={currentTheme?.theme?.font_color}
              >
                {t("admin.apiIntegration.tfcodeTitle")}
              </AppText>
              {tfcodeStatus?.exists && !tfcodeStatus?.loading && (
                <AppText
                  wrapperStyle={{
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                    borderRadius: 20,
                    backgroundColor: currentTheme?.theme?.primary_soft,
                  }}
                  color={currentTheme?.theme?.primary}
                  fontSize={12}
                  fontWeight="bold"
                >
                  {t("admin.apiIntegration.tfcodeActiveBadge")}
                </AppText>
              )}
            </View>

            <AppText wrapperStyle={{ marginTop: 10, marginBottom: 4 }}>
              {t("admin.apiIntegration.tfcodeExplainer")}
            </AppText>

            {isTfcodeContentLoading ? (
              <View
                style={{
                  marginVertical: 14,
                  padding: 14,
                  borderRadius: 8,
                  backgroundColor:
                    currentTheme?.theme?.message_bubble_background,
                  alignItems: "center",
                }}
              >
                <AppActivityIndicator
                  visible
                  size={20}
                  containerStyle={{
                    backgroundColor: currentTheme?.theme?.transparent,
                  }}
                />
              </View>
            ) : (
              <View
                style={{
                  marginVertical: 14,
                  padding: 14,
                  borderRadius: 8,
                  backgroundColor:
                    currentTheme?.theme?.message_bubble_background,
                }}
              >
                <AppText
                  wrapperStyle={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    maxWidth: "100%",
                  }}
                  color={currentTheme?.theme?.primary}
                >
                  {tfcodeContent ?? tfcodeJsonSnippet}
                </AppText>
              </View>
            )}

            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 10 }}
            >
              <ToothFairyButton
                title={t("admin.apiIntegration.tfcodeUploadButton")}
                onPress={triggerTfcodeUpload}
                isDisabled={isTfcodeUploading}
                style={{ marginHorizontal: 0 }}
              />
              <ToothFairyButton
                title={t("admin.apiIntegration.tfcodeDownloadButton")}
                onPress={handleTfcodeDownload}
                isDisabled={isTfcodeDownloading || !tfcodeStatus?.exists}
                isSecondary
                fontColor={currentTheme?.theme?.primary}
                style={{ marginHorizontal: 0 }}
              />
            </View>

            {isTfcodeUploading && (
              <AppText
                wrapperStyle={{ marginTop: 12, textAlign: "center" }}
                color={currentTheme?.theme?.primary}
                fontWeight="bold"
              >
                {`${t(
                  "admin.apiIntegration.tfcodeUploading",
                )} ${tfcodeUploadProgress}%`}
              </AppText>
            )}

            <View
              style={{
                marginTop: 14,
                minHeight: 24,
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {tfcodeStatus?.loading ? (
                <AppActivityIndicator
                  visible
                  size={20}
                  containerStyle={{
                    backgroundColor: currentTheme?.theme?.transparent,
                  }}
                />
              ) : (
                <AppText color={currentTheme?.theme?.darkGrey} fontSize={13}>
                  {tfcodeStatus?.exists && tfcodeStatus?.lastModified
                    ? t("admin.apiIntegration.tfcodeLastChanged", {
                        date: dayjs(tfcodeStatus.lastModified).format(
                          "DD MMM YYYY, HH:mm",
                        ),
                      })
                    : t("admin.apiIntegration.tfcodeNotUploaded")}
                </AppText>
              )}
            </View>
          </View>

          <View
            style={{
              alignSelf: "stretch",
              width: "100%",
              marginTop: 12,
              padding: 16,
              borderRadius: 12,
              backgroundColor: currentTheme?.theme?.cardBackground,
              marginHorizontal: 10,
            }}
          >
            <AppText
              wrapperStyle={{ marginBottom: 6 }}
              fontWeight="bold"
              fontSize={16}
              color={currentTheme?.theme?.font_color}
            >
              {t("admin.apiIntegration.connectTitle")}
            </AppText>
            <AppText wrapperStyle={{ marginBottom: 4 }}>
              {t("admin.apiIntegration.connectSubCaption", { url: baseUrl })}
            </AppText>

            <View style={{ width: "100%", maxWidth: 750, alignSelf: "center" }}>
              <AppTab
                routes={connectRoutes}
                renderSceneObject={connectScenes}
                isAnimated={false}
                tabMinWidth={90}
                nonAnimatedTabViewStyle={{
                  width: "100%",
                  flexWrap: "nowrap",
                  gap: 2,
                  justifyContent: "flex-start",
                }}
                appTabWrapperStyle={{ marginRight: 0 }}
              />
            </View>
          </View>
        </AppExpander>
      </View>

      <ToothFairyModal
        isModalOpen={showRotateModal}
        setIsModalOpen={setShowRotateModal}
        closeModal={() => setShowRotateModal(false)}
        modalStyle={{ height: "auto", maxHeight: 400 }}
      >
        <View style={{ padding: 20, alignItems: "center" }}>
          <AppText
            wrapperStyle={{ marginBottom: 20 }}
            fontWeight="bold"
            fontSize={18}
          >
            {t("admin.apiIntegration.rotateWarningTitle")}
          </AppText>
          <AppText wrapperStyle={{ marginBottom: 20, textAlign: "center" }}>
            {t("admin.apiIntegration.rotateWarningMessage")}
          </AppText>
          <View style={{ flexDirection: "row", gap: 10 }}>
            <ToothFairyButton
              title={t("common.cancel")}
              onPress={() => setShowRotateModal(false)}
              isSecondary
            />
            <ToothFairyButton
              title={
                isRotating
                  ? t("common.loading")
                  : t("admin.apiIntegration.confirmRotate")
              }
              onPress={handleRotateApiKey}
              isDisabled={isRotating}
              backgroundColor={currentTheme?.theme?.red}
              fontColor={currentTheme?.theme?.white}
            />
          </View>
        </View>
      </ToothFairyModal>
    </ScrollView>
  );
};

export default ApiIntegrationView;
